import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, User, DollarSign } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import Layout from './Layout';

interface Founder {
  id: number;
  firstName: string;
  lastName: string;
  profilePictureUrl?: string;
}

interface FundedStartup {
  id: number;
  startupName: string;
  shortDescription: string;
  requestedFundingAmount: number;
  actualFundingAmount: number;
  labels: string[];
  fundingDate: string;
  investorInfo?: string;
  founder: Founder;
}

interface Pagination {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface ApiResponse {
  fundedStartups: FundedStartup[];
  pagination: Pagination;
}

const FundedPage: React.FC = () => {
  const navigate = useNavigate();
  const { themeClasses, isLightTheme } = useTheme();
  const [fundedStartups, setFundedStartups] = useState<FundedStartup[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    fetchFundedStartups();
  }, [page]);

  const fetchFundedStartups = async () => {
    try {
      setLoading(true);
      const response = await api.get<ApiResponse>('/funded', {
        params: { page, limit: 10 }
      });
      
      const { fundedStartups: newStartups, pagination } = response.data;
      
      if (page === 1) {
        setFundedStartups(newStartups);
      } else {
        setFundedStartups(prev => [...prev, ...newStartups]);
      }
      
      setHasMore(pagination.hasNext);
    } catch (error) {
      console.error('Error fetching funded startups:', error);
      toast.error('Failed to load funded startups');
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      setPage(prev => prev + 1);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Layout title="Founders who got fund" showBackButton onBack={() => navigate('/')}>

      {/* Funded Startups List */}
      <div className="p-4 space-y-4">
        {fundedStartups.map((startup) => (
          <div key={startup.id} className={`${themeClasses.card} rounded-lg`}>
            <div className="flex items-start space-x-4">
              {/* Founder Avatar */}
              <div className={`w-16 h-16 ${isLightTheme ? 'bg-gray-300' : 'bg-gray-600'} rounded-full flex items-center justify-center flex-shrink-0`}>
                {startup.founder.profilePictureUrl ? (
                  <img
                    src={startup.founder.profilePictureUrl}
                    alt={`${startup.founder.firstName} ${startup.founder.lastName}`}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                ) : (
                  <User className={`w-8 h-8 ${themeClasses.textSecondary}`} />
                )}
              </div>

              {/* Startup Info */}
              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className={`text-lg font-semibold ${themeClasses.text}`}>{startup.startupName}</h3>
                    <p className={`${themeClasses.textSecondary} text-sm`}>
                      by {startup.founder.firstName} {startup.founder.lastName}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center text-green-400">
                      <DollarSign className="w-4 h-4 mr-1" />
                      <span className="font-semibold">{formatCurrency(startup.actualFundingAmount)}</span>
                    </div>
                    <p className={`${themeClasses.textSecondary} text-xs`}>Funded</p>
                  </div>
                </div>

                <p className={`${themeClasses.textSecondary} text-sm mb-3`}>{startup.shortDescription}</p>

                <div className="flex items-center justify-between">
                  <div className="flex space-x-2">
                    {startup.labels.map((label) => (
                      <span
                        key={label}
                        className={`px-2 py-1 ${isLightTheme ? 'bg-gray-200 text-gray-700' : 'bg-gray-700 text-gray-300'} text-xs rounded-full`}
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                  <span className={`${themeClasses.textSecondary} text-xs`}>
                    {formatDate(startup.fundingDate)}
                  </span>
                </div>

                {startup.investorInfo && (
                  <div className={`mt-3 pt-3 border-t ${themeClasses.border}`}>
                    <p className={`${themeClasses.textSecondary} text-sm`}>
                      <span className="font-medium">Investor:</span> {startup.investorInfo}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Load More Button */}
        {hasMore && (
          <div className="text-center">
            <button
              onClick={loadMore}
              disabled={loading}
              className={`${themeClasses.buttonSecondary} px-6 py-2 rounded-lg font-medium transition-colors duration-200`}
            >
              {loading ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && fundedStartups.length === 0 && (
          <div className="text-center py-12">
            <TrendingUp className={`w-16 h-16 ${themeClasses.textSecondary} mx-auto mb-4`} />
            <p className={themeClasses.textSecondary}>No funded startups yet</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default FundedPage; 